import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { Input, Button, Dropdown } from "components";

import { useTheme } from "context";

import { useAppDispatch, useAppSelector } from "store/store";
import { setSurveyGenerateType } from "store/create-survey/survey";
import { hideModal } from "store/modal";
import { createSurveyWithAI } from "store/create-survey/survey/actions";

import { selectCombinedData } from "./selectors";
import { isErrorResult } from "services";
import { ROUTES } from "router/routes";

import { AIFormInputs } from "./types";
import { CreateSurveyResponse } from "store/create-survey/survey/types";
import { aiModels } from "pages/discover-survey/constants";

const GenerateSurveyModal: React.FC = () => {
  const { pending, isQuiz } = useAppSelector(selectCombinedData);

  const { theme } = useTheme();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useForm<AIFormInputs>({
    defaultValues: {
      aiModel: "OPENAI"
    }
  });

  const [keyword, questionCount, aiModel] = watch(["keyword", "questionCount", "aiModel"]);

  const handleRemoveSurveyType = () => {
    dispatch(hideModal());
    dispatch(setSurveyGenerateType());
  };

  const handleSubmit = () => {
    dispatch(createSurveyWithAI({ keyword, questionCount: +questionCount, isQuiz, aiModel })).then(res => {
      if (!isErrorResult(res.payload)) {
        const { id } = res.payload as CreateSurveyResponse;
        navigate(`/${ROUTES.business}/${ROUTES.surveys}/${id}/${ROUTES.surveySettings}`);
        handleRemoveSurveyType();
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to create survey:', res.payload);
      }
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Error creating survey:', error);
    });
  };

  return (
    <div className="flex flex-col justify-center w-full">
      <p className={clsx("text-sm", theme === "light" ? "text-zinc-600" : "text-zinc-400")}>
        {`Generate a ${isQuiz ? "quiz" : "survey"} using AI (Machine Learning) based on a keyword you select. Try it out!`}
      </p>

      <div className="grid grid-cols-12 gap-3 mt-6">
        <div className="col-span-6 sm:col-span-7">
          <label
            htmlFor="first-name"
            className={clsx("mb-1.5 block text-xs", theme === "light" ? "text-zinc-900" : "text-white")}
          >
            {`About what should be this ${isQuiz ? "quiz" : "survey"}?`}
          </label>
          <Input
            placeholder="type a keyword"
            disabled={pending}
            {...register("keyword", {
              required: true,
              validate: {
                validateentry: value =>
                  /^[\D\w]{4,}$/.test(value) ||
                  "Keyword should contain letters and/or numbers with at least 4 characters long",
              },
            })}
            style={{ color: theme === "light" ? "#09090B" : "white" }}
          />
          {errors.keyword && <span className="p-0 text-xs font-semibold text-red-500">{errors.keyword.message}</span>}
        </div>

        <div className="col-span-6 sm:col-span-5">
          <label
            htmlFor="last-name"
            className={clsx("mb-1.5 block text-xs", theme === "light" ? "text-zinc-900" : "text-white")}
          >
            Questions Number (30 max)
          </label>
          <Input
            placeholder="Number of questions"
            required
            disabled={pending}
            type="number"
            min={1}
            max={30}
            {...register("questionCount", {
              required: true,
              validate: {
                minValue: value => value > 0 || "Must be a least 1",
                maxValue: value => value <= 30 || "Must be at max. 30",
              },
            })}
            style={{ color: theme === "light" ? "#09090B" : "white" }}
          />
          {errors.questionCount && (
            <span className="text-xs font-semibold text-red-500">{errors.questionCount.message}</span>
          )}
        </div>
        <div className="col-span-12">
          <label
            htmlFor="ai-model"
            className={clsx("mb-1.5 block text-xs", theme === "light" ? "text-zinc-900" : "text-white")}
          >
            Select AI Model
          </label>
          <Controller
            name="aiModel"
            control={control}
            defaultValue="OPENAI"
            render={({ field }) => (
              <Dropdown
                key="models"
                value={field.value}
                options={aiModels}
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
        {errors.aiModel && (
          <span className="text-xs font-semibold text-red-500">{errors.aiModel.message}</span>
        )}
      </div>

      <div className="gap-3 mt-5 sm:mt-4 sm:flex">
        <Button
          pending={pending}
          disabled={!keyword || !questionCount || !aiModel}
          onClick={handleSubmit}
        >{`Generate ${isQuiz ? "Quiz" : "Survey"}`}</Button>

        <Button variant="super" disabled={pending} onClick={handleRemoveSurveyType}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default GenerateSurveyModal;
